// frontend/src/pages/Modules.tsx
import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  CheckCircle,
  PlayCircle,
  Clock,
  ArrowLeft,
  Lock,
  RefreshCw
} from 'lucide-react';
import api from '../services/api';
import toast from 'react-hot-toast';
import HeyGenAgent from '../components/HeyGenAgent';

interface Module {
  id: number;
  titre: string;
  description: string;
  ordre: number;
  video_url: string;
  duree_minutes: number;
  status: 'not_started' | 'in_progress' | 'completed';
  time_spent: number;
}

export default function Modules() {
  const { promotionId } = useParams();
  const navigate = useNavigate();
  const [modules, setModules] = useState<Module[]>([]);
  const [loading, setLoading] = useState(true);
  const [promotion, setPromotion] = useState<any>(null);

  useEffect(() => {
    fetchModules();
  }, [promotionId]);

  const fetchModules = async () => {
    try {
      // Récupérer les modules
      const modulesRes = await api.get(`/modules/promotion/${promotionId}`);
      setModules(modulesRes.data);
      
      // Récupérer les infos de la promotion
      const promotionsRes = await api.get('/promotions/my-promotions');
      const currentPromotion = promotionsRes.data.find(
        (p: any) => p.id === parseInt(promotionId!)
      );

      if (currentPromotion) {
        // Le produit devrait déjà être inclus dans la réponse grâce à joinedload
        if (currentPromotion.product) {
          if (!currentPromotion.product.heygen_agent_id) {
            console.warn('Aucun heygen_agent_id trouvé pour le produit:', currentPromotion.product_id);
            toast.error('Aucun agent HeyGen configuré pour cette formation');
          }

          setPromotion(currentPromotion);
        } else {
          console.warn('Product non inclus dans la promotion, fallback vers API call');
          
          try {
            // Fallback: utiliser le product_id de la promotion pour récupérer l'agent HeyGen
            const productRes = await api.get(`/products/${currentPromotion.product_id}`);
            const product = productRes.data;
            
            console.log('Product retrieved from API (fallback):', product);
            console.log('HeyGen Agent ID from product (fallback):', product.heygen_agent_id);

            if (!product.heygen_agent_id) {
              console.error('❌ ERREUR: Aucun heygen_agent_id trouvé via fallback');
              console.error('Product ID:', currentPromotion.product_id);
              console.error('Product data:', product);
              console.error('Vérifiez que la table products contient bien un heygen_agent_id pour ce produit');
              
              toast.error('Aucun agent HeyGen configuré pour cette formation');
            } else {
              console.log('✅ SUCCESS: HeyGen Agent ID trouvé via fallback:', product.heygen_agent_id);
              toast.success(`Agent HeyGen configuré: ${product.heygen_agent_id.substring(0, 20)}...`);
            }

            setPromotion({ ...currentPromotion, product });
          } catch (error) {
            console.error('❌ ERREUR lors de la récupération du produit (fallback):');
            console.error('Product ID:', currentPromotion.product_id);
            console.error('Error details:', error);
            console.error('Vérifiez que le produit existe dans la base de données');
            
            toast.error('Erreur lors de la récupération des informations du produit');
            setPromotion(currentPromotion);
          }
        }
      } else {
        console.error('❌ ERREUR: Promotion non trouvée');
        console.error('Promotion ID recherché:', promotionId);
        console.error(
          'Promotions disponibles:',
          promotionsRes.data.map((p: any) => ({ id: p.id, nom: p.nom }))
        );
        toast.error('Promotion non trouvée');
            }
    } catch (error) {
      console.error('❌ ERREUR GLOBALE lors du chargement des modules:', error);
      toast.error('Impossible de charger les modules');
    } finally {
      setLoading(false);
    }
  };

  const canStartModule = (module: Module): boolean => {
    // Vérifier que tous les modules précédents sont terminés
    const previousModules = modules.filter(m => m.ordre < module.ordre);
    return previousModules.every(m => m.status === 'completed');
  };

  const getModuleIcon = (module: Module) => {
    if (module.status === 'completed') {
      return <CheckCircle className="h-6 w-6 text-green-500" />;
    } else if (module.status === 'in_progress') {
      return <RefreshCw className="h-6 w-6 text-yellow-500 animate-spin" />;
    } else if (canStartModule(module)) {
      return <PlayCircle className="h-6 w-6 text-primary" />;
    } else {
      return <Lock className="h-6 w-6 text-gray-400" />;
    }
  };

  const allModulesCompleted = modules.every(m => m.status === 'completed');

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  const completedCount = modules.filter(m => m.status === 'completed').length;
  const progressPercent =
    modules.length > 0 ? (completedCount / modules.length) * 100 : 0;

  return (
    <div className="space-y-6 lg:space-y-8">
      <HeyGenAgent heygenAgentUrl={promotion?.product?.heygen_agent_id} />
      {/* Panneau de débogage (développement uniquement) */}
      {import.meta.env.DEV && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <h3 className="text-sm font-medium text-yellow-800 mb-2">Debug Info (Dev Only)</h3>
          <div className="text-xs text-yellow-700 space-y-1">
            <div><strong>Promotion ID:</strong> {promotionId}</div>
            <div><strong>Promotion loaded:</strong> {promotion ? 'Oui' : 'Non'}</div>
            <div><strong>Product in promotion:</strong> {promotion?.product ? 'Oui' : 'Non'}</div>
            <div><strong>Product ID:</strong> {promotion?.product_id || promotion?.product?.id || 'Non trouvé'}</div>
            <div><strong>HeyGen Agent ID:</strong> {promotion?.product?.heygen_agent_id || 'Non trouvé'}</div>
            <div><strong>Product nom:</strong> {promotion?.product?.nom || 'Non trouvé'}</div>
          </div>
        </div>
      )}

      {/* En-tête */}
      <div className="bg-white rounded-lg shadow p-4 sm:p-6">
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 sm:gap-6">
          <div className="min-w-0">
            <Link
              to="/promotions"
              className="flex items-center text-sm text-gray-500 hover:text-gray-700 mb-1 sm:mb-2"
            >
              <ArrowLeft className="h-4 w-4 mr-1" />
              Retour aux promotions
            </Link>
            <h1 className="text-xl sm:text-2xl font-bold text-gray-900 break-words">
              {promotion?.product?.nom || 'Modules de formation'}
            </h1>
            <p className="mt-1 text-gray-600 break-words">
              {promotion?.nom}
            </p>
          </div>
          <div className="text-left sm:text-right">
            <p className="text-sm text-gray-500">Progression</p>
            <p className="text-2xl font-bold text-primary">
              {completedCount}/{modules.length}
            </p>
          </div>
        </div>

        {/* Barre de progression */}
        <div className="mt-4 sm:mt-6">
          <div className="w-full bg-gray-200 rounded-full h-2.5">
            <div
              className="bg-primary h-2.5 rounded-full transition-all duration-300"
              style={{ width: `${progressPercent}%` }}
            />
          </div>
        </div>
      </div>

      {/* Liste des modules */}
      <div className="space-y-4 lg:space-y-6">
        {modules.map((module) => {
          const isLocked = !canStartModule(module) && module.status === 'not_started';
          
          return (
            <div
              key={module.id}
              className={`bg-white rounded-lg shadow p-4 sm:p-6 ${
                isLocked ? 'opacity-60' : 'hover:shadow-lg transition-shadow'
              }`}
            >
              <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4 md:gap-6">
                <div className="flex items-start space-x-3 sm:space-x-4">
                  <div className="flex-shrink-0 mt-1">
                    {getModuleIcon(module)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="text-base sm:text-lg font-semibold text-gray-900 break-words">
                      Module {module.ordre}: {module.titre}
                    </h3>
                    {module.description && (
                      <p className="mt-1 text-gray-600 break-words">{module.description}</p>
                    )}
                    <div className="flex flex-wrap items-center gap-2 sm:gap-4 mt-3 text-sm text-gray-500">
                      <div className="flex items-center">
                        <Clock className="h-4 w-4 mr-1" />
                        {module.duree_minutes} minutes
                      </div>
                      {module.time_spent > 0 && (
                        <div className="flex items-center">
                          <PlayCircle className="h-4 w-4 mr-1" />
                          {Math.floor(module.time_spent / 60)} min passées
                        </div>
                      )}
                      {module.status === 'completed' && (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                          Terminé
                        </span>
                      )}
                    </div>
                  </div>
                </div>

                <div className="md:ml-4">
                  {isLocked ? (
                    <button
                      disabled
                      className="w-full md:w-auto inline-flex justify-center items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-400 bg-gray-100 cursor-not-allowed"
                    >
                      <Lock className="h-4 w-4 mr-2" />
                      Verrouillé
                    </button>
                  ) : (
                    <Link
                      to={`/modules/${module.id}?promotionId=${promotionId}`}
                      className="w-full md:w-auto inline-flex justify-center items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-primary hover:bg-primary-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
                    >
                      {module.status === 'completed' ? 'Revoir' : 
                       module.status === 'in_progress' ? 'Continuer' : 'Commencer'}
                    </Link>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Accès au quiz si tous les modules sont terminés */}
      {allModulesCompleted && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4 sm:p-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-6">
            <div>
              <h3 className="text-base sm:text-lg font-semibold text-green-900">
                Félicitations ! Vous avez terminé tous les modules
              </h3>
              <p className="mt-1 text-green-700">
                Vous pouvez maintenant passer le quiz de validation
              </p>
            </div>
            <Link
              to={`/quiz/${promotionId}`}
              className="w-full sm:w-auto inline-flex justify-center items-center px-6 py-3 border border-transparent text-base font-medium rounded-md text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
            >
              Passer le quiz
            </Link>
          </div>
        </div>
      )}

    </div>
  );
}
